import React from 'react';
import PropTypes from 'prop-types';

import { Icon } from '@ontruck/orc';
import LiteralCurrency from 'components/LiteralCurrency';
import Tag from 'components/Tag';
import TagTooltipContainer from './components/TagTooltipContainer';
import Tooltip from 'components/Tooltip';
import { GirdedQuestionIcon } from 'refactored/ui/icons';
import useIsSpainCountry from 'hooks/useIsSpainCountry';
import useGetCurrentQuote from 'hooks/useGetCurrentQuote';
import extrasConstants from 'utils/extras/constants/index';
import { useTranslation } from 'react-i18next';

const PriceConceptsBreakdown = ({
  className,
  concepts,
  collapsable,
  showTooltips,
  showQuantity,
  hasHandlingExtrasInDetail,
}) => {
  const { t } = useTranslation('Quote');
  const extrasInQuote = useGetCurrentQuote().shipments_extras || {};
  const hasHandlingExtrasInQuote = (extrasInQuote[0] || []).some((_, i) =>
    extrasConstants.HANDLING_EXTRAS.includes(
      (extrasInQuote[0][i] || {}).concept
    )
  );
  const isSpainCountry = useIsSpainCountry();
  const showSpecialHandlingLabel =
    isSpainCountry && (hasHandlingExtrasInQuote || hasHandlingExtrasInDetail);

  return (
    <div className={className}>
      <ul className="concepts-list">
        {concepts.map((c, i) => (
          <li className="concept-item" key={c.concept}>
            <div>
              {c.strikethrough ? <del>{c.concept}</del> : c.concept}
              {i === 0 && showSpecialHandlingLabel && (
                <p className="handling-tip">{t('handling_included')}</p>
              )}
              {c.tagLabel && (
                <Tag className={c.tagLabelClassName}>
                  {c.tagLabel}
                  {c.tagLabelInfoTooltipMessage && (
                    <Tooltip
                      component={
                        <TagTooltipContainer>
                          {c.tagLabelInfoTooltipMessage}
                        </TagTooltipContainer>
                      }
                    >
                      <Icon glyph="info" size="1.6rem" />
                    </Tooltip>
                  )}
                </Tag>
              )}
              {c.tooltipInnerComponent() && (!collapsable || showTooltips) && (
                <Tooltip
                  className="tooltip"
                  component={c.tooltipInnerComponent()}
                  skin="light"
                  position="bottom"
                  segmentTitle="price breakdown"
                  segmentData={{ concept: c.concept }}
                >
                  <GirdedQuestionIcon />
                </Tooltip>
              )}
            </div>
            <div>
              {showQuantity && (
                <LiteralCurrency
                  value={c.conceptQuantity}
                  strikethrough={c.strikethrough}
                />
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

PriceConceptsBreakdown.propTypes = {
  className: PropTypes.string,
  concepts: PropTypes.arrayOf(
    PropTypes.shape({
      concept: PropTypes.string.isRequired,
      tooltipInnerComponent: PropTypes.func,
      conceptQuantity: PropTypes.number.isRequired,
    })
  ),
  collapsable: PropTypes.bool,
  showTooltips: PropTypes.bool,
  showQuantity: PropTypes.bool,
  hasHandlingExtrasInDetail: PropTypes.bool,
};

PriceConceptsBreakdown.defaultProps = {
  className: '',
  concepts: [],
  collapsable: true,
  showTooltips: false,
  showQuantity: true,
  hasHandlingExtrasInDetail: false,
};

export default PriceConceptsBreakdown;
